import {DateRange} from '@blueprintjs/datetime2';
import {all, create} from 'mathjs';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  AlertPolicy,
  AlertThresholdDirection,
  ApiAlertDateRange,
  CategoryAlertProperties,
  CompositeAlertProperties,
  OverlayFilter,
  OverlayListKey,
  RangeCondition,
  ThresholdAlertProperties,
  VegetationDropAlertProperties,
} from 'app/modules/Remote/Feature/types';
import {
  CUSTOM_ALERT_TYPE_CATEGORY,
  CUSTOM_ALERT_TYPE_COMPOSITE,
  CUSTOM_ALERT_TYPE_THRESHOLD,
  CUSTOM_ALERT_TYPE_VEGETATION_DROP,
} from 'app/utils/constants';
import {DataLayerInfo, ImageLayerInfo, S2_NDVI} from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';

export interface GenericFormState {
  name: string;
  description: string;
  enrolledIds: number[];
}
export type CategoryAlertFormState = GenericFormState & {
  alertType: typeof CUSTOM_ALERT_TYPE_CATEGORY;
  layer: ImageLayerInfo;
  properties: CategoryAlertProperties;
};
export type ThresholdAlertFormState = GenericFormState & {
  alertType: typeof CUSTOM_ALERT_TYPE_THRESHOLD;
  layer: DataLayerInfo;
  properties: ThresholdAlertProperties;
};
export type VegetationDropAlertFormState = GenericFormState & {
  alertType: typeof CUSTOM_ALERT_TYPE_VEGETATION_DROP;
  layer: DataLayerInfo;
  properties: VegetationDropAlertProperties;
};
export type CompositeAlertFormState = GenericFormState & {
  alertType: typeof CUSTOM_ALERT_TYPE_COMPOSITE;
  // The layer an alert opens on: the first condition's, kept in step with it by the reducer.
  layer: DataLayerInfo;
  properties: CompositeAlertProperties;
  // Stable per-group React keys, positionally aligned with properties.groups. Lives on the form
  // state rather than properties so it never reaches the API. A group's own array is replaced on
  // every edit inside it (new reference each keystroke), so identity can't be derived from that.
  conditionGroupIds: string[];
};
export type AlertFormState =
  | CategoryAlertFormState
  | ThresholdAlertFormState
  | VegetationDropAlertFormState
  | CompositeAlertFormState;

export const MAX_CONDITIONS_PER_GROUP = 6;
export const MAX_CONDITION_GROUPS = 6;
// Across both lists; the API refuses more.
export const MAX_OVERLAYS_PER_POLICY = 5;

type ActionTypeMap = {
  SET_THRESHOLD_SLIDER_VALUE: {value: number};
  SET_THRESHOLD_DIRECTION: {direction: AlertThresholdDirection};
  SET_THRESHOLD_PERCENTAGE: {value: number};
  SET_NUM_SCENES: {value: number};
  SET_LAYER: {layer: {key: string}};
  SET_ALERT_TYPE: {
    alertType: typeof CUSTOM_ALERT_TYPE_VEGETATION_DROP | typeof CUSTOM_ALERT_TYPE_COMPOSITE;
    layerKey?: string;
  };
  ADD_CONDITION_GROUP: object;
  REMOVE_CONDITION_GROUP: {groupIndex: number};
  ADD_CONDITION: {groupIndex: number; layerKey: string};
  REMOVE_CONDITION: {groupIndex: number; conditionIndex: number};
  SET_CONDITION_LAYER: {groupIndex: number; conditionIndex: number; layerKey: string};
  SET_CONDITION_RANGE: {
    groupIndex: number;
    conditionIndex: number;
    min: number | null;
    max: number | null;
  };
  SET_NAME: {name: string};
  SET_DESCRIPTION: {description: string};
  SET_IDS_TO_ENROLL: {enrolledIds: number[]};
  SET_TO_CATEGORIES: {categories: (number | string)[]};
  SET_FROM_CATEGORIES: {categories: (number | string)[]};
  ADD_OVERLAY: {list: OverlayListKey; featureCollectionId: number};
  REMOVE_OVERLAY: {list: OverlayListKey; featureCollectionId: number};
  SET_OVERLAY_FILTER: {list: OverlayListKey; featureCollectionId: number; filter: OverlayFilter | null};
  CLEAR_OVERLAYS: object;
  SET_MINIMUM_ALERTING_AREA: {value: number | null};
  SET_DATE_RANGE: {dateRange: DateRange | undefined};
  RESET: object;
  CLOSE: object;
};

export type AlertFormAction = {
  [K in keyof ActionTypeMap]: {type: K} & ActionTypeMap[K];
}[keyof ActionTypeMap];

export const getDefaultVegetationDropFormState = (): VegetationDropAlertFormState => {
  const layer = layerUtils.getLayer(S2_NDVI) as DataLayerInfo;
  return {
    properties: {
      overlayFeatureCollectionId: null,
      overlayMethod: null,
      includeOverlays: [],
      excludeOverlays: [],
      minimumAlertingArea: 0,
      dateRange: undefined,
    },
    layer: layer,
    alertType: CUSTOM_ALERT_TYPE_VEGETATION_DROP,
    name: '',
    description: '',
    enrolledIds: [],
  };
};

// Blank by default: a seeded bound (e.g. NDVI's dataRange[0] of 0) reads as a real decision and
// passes validateCompositeStep on arrival, so a composite could fire on every pixel without the
// user touching a control. `id` defaults to a fresh one; pass the row's existing id when a layer
// change should keep the same row rather than start a new one.
const conditionForLayer = (layerKey: string, id: string = uuidv4()): RangeCondition => {
  const layer = layerUtils.getLayer(layerKey) as DataLayerInfo;
  const {layerId} = layerUtils.parseLayerKey(layer.key);
  return {id, layerId, min: null, max: null};
};

/**
 * Applies a change to a composite policy's groups, dropping any group the change emptied and keeping
 * formState.layer on the first condition's layer — that's the one an alert opens on, and it's what
 * the create call sends as the policy's source and layer.
 *
 * conditionGroupIds is carried through in lockstep: transformIds defaults to leaving it untouched
 * (the common case — an edit inside a group doesn't add or remove groups), and both it and the
 * groups transform are filtered by the same predicate below so a dropped group's id is dropped
 * with it. A transform that changes group count (add/remove group) must pass a matching transformIds.
 */
const withCompositeGroups = (
  formState: AlertFormState,
  transform: (groups: RangeCondition[][]) => RangeCondition[][],
  transformIds: (groupIds: string[]) => string[] = (groupIds) => groupIds
): AlertFormState => {
  if (formState.alertType !== CUSTOM_ALERT_TYPE_COMPOSITE) return formState;

  const transformedGroups = transform(formState.properties.groups);
  const transformedIds = transformIds(formState.conditionGroupIds);
  const kept = transformedGroups
    .map((group, index) => ({group, id: transformedIds[index]}))
    .filter(({group}) => group.length > 0);
  if (!kept.length) return formState;

  const groups = kept.map(({group}) => group);
  const conditionGroupIds = kept.map(({id}) => id);

  const {sourceId} = layerUtils.parseLayerKey(formState.layer.key);
  const displayLayer = layerUtils.getLayer(
    layerUtils.getLayerKeyFromSourceAndLayer(sourceId, groups[0][0].layerId)
  ) as DataLayerInfo;

  return {
    ...formState,
    layer: displayLayer,
    conditionGroupIds,
    properties: {...formState.properties, groups},
  };
};

export const getDefaultCompositeFormState = (layerKey: string): CompositeAlertFormState => {
  return {
    properties: {
      groups: [[conditionForLayer(layerKey)]],
      percentOfProperty: 0,
      numConsecutiveScenes: 1,
      overlayFeatureCollectionId: null,
      overlayMethod: null,
      includeOverlays: [],
      excludeOverlays: [],
      minimumAlertingArea: 0,
      dateRange: undefined,
    },
    conditionGroupIds: [uuidv4()],
    layer: layerUtils.getLayer(layerKey) as DataLayerInfo,
    alertType: CUSTOM_ALERT_TYPE_COMPOSITE,
    name: '',
    description: '',
    enrolledIds: [],
  };
};

export const getDefaultFormState = (layerKey: string): AlertFormState => {
  const layer = layerUtils.getLayer(layerKey);

  return layer.type === 'data'
    ? {
        properties: {
          value: layer.dataRange[0],
          direction: AlertThresholdDirection.Above,
          percentOfProperty: 0,
          numConsecutiveScenes: 3,
          overlayFeatureCollectionId: null,
          overlayMethod: null,
          includeOverlays: [],
          excludeOverlays: [],
          minimumAlertingArea: 0,
          dateRange: undefined,
        },
        layer: layer as DataLayerInfo,
        alertType: CUSTOM_ALERT_TYPE_THRESHOLD,
        name: '',
        description: '',
        enrolledIds: [],
      }
    : {
        properties: {
          fromCategories: [],
          toCategories: [],
          overlayFeatureCollectionId: null,
          overlayMethod: null,
          includeOverlays: [],
          excludeOverlays: [],
          minimumAlertingArea: 0,
          dateRange: undefined,
        },
        layer: layer as ImageLayerInfo,
        alertType: CUSTOM_ALERT_TYPE_CATEGORY,
        name: '',
        description: '',
        enrolledIds: [],
      };
};

export const getFormStateForAlertPolicy = (alertPolicy: AlertPolicy): AlertFormState => {
  const layerKey = layerUtils.getLayerKeyFromSourceAndLayer(
    alertPolicy.sourceId,
    alertPolicy.layerId
  );
  const layer = layerUtils.getLayer(layerKey);

  const math = create(all, {
    number: 'BigNumber',
  });

  const formattedDateRange: DateRange | undefined = (() => {
    const oneIndexedDateRange = alertPolicy.properties.dateRange as unknown as ApiAlertDateRange;
    if (!oneIndexedDateRange || !oneIndexedDateRange[0] || !oneIndexedDateRange[1])
      return undefined;
    const anchorYear = new Date().getFullYear();
    const startDate = new Date(
      anchorYear,
      oneIndexedDateRange[0].month - 1,
      oneIndexedDateRange[0].day
    );
    const endDate = new Date(
      oneIndexedDateRange[1].month < oneIndexedDateRange[0].month ? anchorYear + 1 : anchorYear,
      oneIndexedDateRange[1].month - 1,
      oneIndexedDateRange[1].day
    );
    return [startDate, endDate];
  })();

  switch (alertPolicy.alertType) {
    case CUSTOM_ALERT_TYPE_THRESHOLD:
      return {
        properties: {
          ...alertPolicy.properties,
          percentOfProperty:
            alertPolicy.properties.percentOfProperty !== undefined
              ? math.number(math.evaluate(`${alertPolicy.properties.percentOfProperty} * 100`))
              : 0,
          numConsecutiveScenes: alertPolicy.properties.numConsecutiveScenes || 1,
          dateRange: formattedDateRange,
        },
        layer: layer as DataLayerInfo,
        alertType: CUSTOM_ALERT_TYPE_THRESHOLD,
        name: alertPolicy.name,
        description: alertPolicy.description,
        enrolledIds: alertPolicy.enrollments.map((enrollment) => enrollment.featureId),
      };
    case CUSTOM_ALERT_TYPE_CATEGORY:
      return {
        properties: {
          ...alertPolicy.properties,
          dateRange: formattedDateRange,
        },
        layer: layer as ImageLayerInfo,
        alertType: CUSTOM_ALERT_TYPE_CATEGORY,
        name: alertPolicy.name,
        description: alertPolicy.description,
        enrolledIds: alertPolicy.enrollments.map((enrollment) => enrollment.featureId),
      };
    case CUSTOM_ALERT_TYPE_VEGETATION_DROP:
      return {
        properties: {
          ...alertPolicy.properties,
          dateRange: formattedDateRange,
        },
        layer: layer as DataLayerInfo,
        alertType: CUSTOM_ALERT_TYPE_VEGETATION_DROP,
        name: alertPolicy.name,
        description: alertPolicy.description,
        enrolledIds: alertPolicy.enrollments.map((enrollment) => enrollment.featureId),
      };
    case CUSTOM_ALERT_TYPE_COMPOSITE:
      return {
        properties: {
          ...alertPolicy.properties,
          // The API has no notion of a condition's client-only id, so a policy loaded from it
          // needs one assigned here before the form can key rows on it.
          groups: alertPolicy.properties.groups.map((group) =>
            group.map((condition) => ({...condition, id: condition.id ?? uuidv4()}))
          ),
          // Stored as a fraction and shown as a percentage, as a threshold's is — but left alone
          // when the policy never set one.
          ...(alertPolicy.properties.percentOfProperty !== undefined
            ? {
                percentOfProperty: math.number(
                  math.evaluate(`${alertPolicy.properties.percentOfProperty} * 100`)
                ),
              }
            : {}),
          dateRange: formattedDateRange,
        },
        conditionGroupIds: alertPolicy.properties.groups.map(() => uuidv4()),
        layer: layer as DataLayerInfo,
        alertType: CUSTOM_ALERT_TYPE_COMPOSITE,
        name: alertPolicy.name,
        description: alertPolicy.description,
        enrolledIds: alertPolicy.enrollments.map((enrollment) => enrollment.featureId),
      };
  }
};

type UseAlertFormArgs = {
  updateMode: boolean;
  alertPolicy?: AlertPolicy;
  availableLayerKeys: string[];
  onClose: () => void;
};

// this will only happen if the user has no analytical layers available.
// we'll show them something other than the form if this happens.
const NO_AVAILABLE_LAYERS_FORM_STATE: AlertFormState = {
  name: '',
  description: '',
  enrolledIds: [],
  layer: {} as DataLayerInfo,
  alertType: 'threshold',
  properties: {} as ThresholdAlertProperties,
};

export function useAlertForm({
  updateMode,
  alertPolicy,
  availableLayerKeys,
  onClose,
}: UseAlertFormArgs) {
  const initialFormState = React.useMemo(
    () =>
      updateMode && alertPolicy
        ? getFormStateForAlertPolicy(alertPolicy)
        : availableLayerKeys.length > 0
          ? getDefaultFormState(availableLayerKeys[0])
          : NO_AVAILABLE_LAYERS_FORM_STATE,
    [updateMode, alertPolicy, availableLayerKeys]
  );

  const reducer = React.useCallback(
    (formState: AlertFormState, action: AlertFormAction): AlertFormState => {
      switch (action.type) {
        case 'SET_THRESHOLD_SLIDER_VALUE':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              value: action.value,
            },
          } as AlertFormState;
        case 'SET_THRESHOLD_DIRECTION':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              direction: action.direction,
            },
          } as AlertFormState;
        case 'SET_THRESHOLD_PERCENTAGE':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              percentOfProperty: action.value,
            },
          } as AlertFormState;
        case 'SET_NUM_SCENES':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              numConsecutiveScenes: action.value,
            },
          } as AlertFormState;
        case 'SET_LAYER':
          return Object.assign(getDefaultFormState(action.layer.key), {
            name: formState.name,
            description: formState.description,
            enrolledIds: formState.enrolledIds,
          }) as AlertFormState;
        case 'SET_ALERT_TYPE': {
          const defaultState =
            action.alertType === CUSTOM_ALERT_TYPE_COMPOSITE
              ? getDefaultCompositeFormState(action.layerKey ?? availableLayerKeys[0])
              : getDefaultVegetationDropFormState();
          return Object.assign(defaultState, {
            name: formState.name,
            description: formState.description,
            enrolledIds: formState.enrolledIds,
          }) as AlertFormState;
        }
        case 'ADD_CONDITION_GROUP':
          return withCompositeGroups(
            formState,
            (groups) =>
              groups.length >= MAX_CONDITION_GROUPS
                ? groups
                : [...groups, [conditionForLayer(formState.layer.key)]],
            (groupIds) => (groupIds.length >= MAX_CONDITION_GROUPS ? groupIds : [...groupIds, uuidv4()])
          );
        case 'REMOVE_CONDITION_GROUP':
          return withCompositeGroups(
            formState,
            (groups) => groups.filter((_, index) => index !== action.groupIndex),
            (groupIds) => groupIds.filter((_, index) => index !== action.groupIndex)
          );
        case 'ADD_CONDITION':
          return withCompositeGroups(formState, (groups) =>
            groups.map((group, index) =>
              index === action.groupIndex && group.length < MAX_CONDITIONS_PER_GROUP
                ? [...group, conditionForLayer(action.layerKey)]
                : group
            )
          );
        case 'REMOVE_CONDITION':
          return withCompositeGroups(formState, (groups) =>
            groups.map((group, index) =>
              index === action.groupIndex
                ? group.filter((_, position) => position !== action.conditionIndex)
                : group
            )
          );
        case 'SET_CONDITION_LAYER':
          return withCompositeGroups(formState, (groups) =>
            groups.map((group, index) =>
              index === action.groupIndex
                ? group.map((condition, position) =>
                    // Picking a new layer is a value change on the same row, not a new row, so it
                    // keeps the row's id.
                    position === action.conditionIndex
                      ? conditionForLayer(action.layerKey, condition.id)
                      : condition
                  )
                : group
            )
          );
        case 'SET_CONDITION_RANGE':
          return withCompositeGroups(formState, (groups) =>
            groups.map((group, index) =>
              index === action.groupIndex
                ? group.map((condition, position) =>
                    position === action.conditionIndex
                      ? {...condition, min: action.min, max: action.max}
                      : condition
                  )
                : group
            )
          );
        case 'SET_NAME':
          return {...formState, name: action.name} as AlertFormState;
        case 'SET_DESCRIPTION':
          return {...formState, description: action.description} as AlertFormState;
        case 'SET_IDS_TO_ENROLL':
          return {...formState, enrolledIds: action.enrolledIds} as AlertFormState;
        case 'SET_TO_CATEGORIES':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              toCategories: action.categories,
            },
          } as AlertFormState;
        case 'SET_FROM_CATEGORIES':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              fromCategories: action.categories,
            },
          } as AlertFormState;
        case 'ADD_OVERLAY': {
          const {includeOverlays, excludeOverlays} = formState.properties;
          const alreadyListed = [...includeOverlays, ...excludeOverlays].some(
            (selection) => selection.featureCollectionId === action.featureCollectionId
          );
          // An overlay in both lists would cancel itself out, and the API refuses both that and
          // more than MAX_OVERLAYS_PER_POLICY.
          if (
            alreadyListed ||
            includeOverlays.length + excludeOverlays.length >= MAX_OVERLAYS_PER_POLICY
          ) {
            return formState;
          }
          return {
            ...formState,
            properties: {
              ...formState.properties,
              [action.list]: [
                ...formState.properties[action.list],
                {featureCollectionId: action.featureCollectionId, filter: null},
              ],
            },
          } as AlertFormState;
        }
        case 'REMOVE_OVERLAY':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              [action.list]: formState.properties[action.list].filter(
                (selection) => selection.featureCollectionId !== action.featureCollectionId
              ),
            },
          } as AlertFormState;
        case 'SET_OVERLAY_FILTER':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              [action.list]: formState.properties[action.list].map((selection) =>
                selection.featureCollectionId === action.featureCollectionId
                  ? // A filter with no values is no filter.
                    {...selection, filter: action.filter?.values.length ? action.filter : null}
                  : selection
              ),
            },
          } as AlertFormState;
        case 'CLEAR_OVERLAYS':
          return {
            ...formState,
            properties: {...formState.properties, includeOverlays: [], excludeOverlays: []},
          } as AlertFormState;
        case 'SET_MINIMUM_ALERTING_AREA':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              minimumAlertingArea: action.value,
            },
          } as AlertFormState;
        case 'SET_DATE_RANGE':
          return {
            ...formState,
            properties: {
              ...formState.properties,
              dateRange: action.dateRange,
            },
          } as AlertFormState;
        case 'RESET':
          return initialFormState;
        case 'CLOSE':
          return getDefaultFormState(availableLayerKeys[0]);
        default:
          throw new Error(`Unhandled action type`);
      }
    },
    [initialFormState, availableLayerKeys]
  );

  const [formState, dispatch] = React.useReducer<React.Reducer<AlertFormState, AlertFormAction>>(
    reducer,
    initialFormState
  );

  const handleReset = React.useCallback(() => {
    // Reset by replacing with initialFormState
    // Then, if data layer, ensure slider value set to min
    dispatch({type: 'RESET'});
    if (!updateMode && 'dataRange' in (formState.layer as DataLayerInfo)) {
      dispatch({
        type: 'SET_THRESHOLD_SLIDER_VALUE',
        value: (formState.layer as DataLayerInfo).dataRange[0],
      });
    }
  }, [updateMode, formState.layer]);

  const handleClose = React.useCallback(() => {
    dispatch({type: 'CLOSE'});
    onClose();
  }, [onClose]);

  return {formState, dispatch, handleReset, handleClose};
}
